/*
    Accepts:
    - readMoreText: String – translation for the read more button
    - readLessText: String – translation for the read less button
    - showCollapsibleText: String (boolean-like true/false) – if the collapse/expand logic will be set/removed
    - visibleLinesAmount: String - visible amount of lines in collapsed mode (works if the `showCollapsibleText` attribute is set to "true")
    - textClassName: String - a class name of the element which will be collapsed/expanded inside of this custom element
    - expandButtonClassName: String - a class name of the element which will trigger collapse/expand inside of this custom element
 */

class CollapsibleDescription extends HTMLElement {
    textCollapsedClassName = 'text--collapsed'
    expandButtonHiddenClassName = 'expand-button--hidden'
    windowWidth = window.innerWidth

    constructor() {
        super();
    }

    connectedCallback() {
        this.readMoreText = this.dataset.readMoreText
        this.readLessText = this.dataset.readLessText
        this.showCollapsibleText = this.dataset.showCollapsibleText
        this.visibleLinesAmount = this.dataset.visibleLinesAmount
        this.textClassName = this.dataset.textClassName
        this.expandButtonClassName = this.dataset.expandButtonClassName

        this.collapsibleTextNode = this.querySelector(`.${this.textClassName}`)
        this.readMoreBtn = this.querySelector(`.${this.expandButtonClassName}`)

        if (this.showCollapsibleText === 'true') {
            this.toggleReadMoreButtonStyles()

            window.addEventListener('resize', (e) => {
                const currentWindowWidth = e.target.innerWidth

                if (this.windowWidth > currentWindowWidth || this.windowWidth < currentWindowWidth) {
                    this.windowWidth = currentWindowWidth

                    this.toggleReadMoreButtonStyles()
                }
            })

            this.readMoreBtn.addEventListener('click', () => {
                if (this.collapsibleTextNode.classList.contains(this.textCollapsedClassName)) {
                    this.readMoreBtn.innerText = this.readLessText
                    this.collapsibleTextNode.classList.remove(this.textCollapsedClassName)
                    this.collapsibleTextNode.style.maxHeight = this.collapsibleTextNode.scrollHeight + 'px'
                } else {
                    this.collapsibleTextNode.style.maxHeight = this.calculateCollapsedHeight({ wrapper: this.collapsibleTextNode, linesAmount: this.visibleLinesAmount }) + 'px'

                    this.readMoreBtn.innerText = this.readMoreText

                    this.collapsibleTextNode.addEventListener('transitionend', () => {
                        this.collapsibleTextNode.classList.add(this.textCollapsedClassName)
                    }, {once: true})
                }
            })
        }
    }

    getBiggestNode (wrapper) {
        const descriptionNodes = wrapper.children
        let biggestNodeHeight = 0

        for (let i = 0; i <= 3; i++) {
            if (descriptionNodes[i] instanceof HTMLElement) {
                const nodeLineHeight = getComputedStyle(descriptionNodes[i]).lineHeight
                const nodeLineHeightInt = parseInt(nodeLineHeight.replace('px', ''))

                if (nodeLineHeightInt && nodeLineHeightInt > biggestNodeHeight) {
                    biggestNodeHeight = nodeLineHeightInt
                }
            }
        }

        return biggestNodeHeight
    }

    calculateCollapsedHeight ({ wrapper, linesAmount = 3 }) {
        const biggestNodeHeight = this.getBiggestNode(wrapper)

        return linesAmount * biggestNodeHeight
    }

    isElementClamped (element) {
        return element.scrollHeight > element.clientHeight
    }

    toggleReadMoreButtonStyles () {
        const calculatedCollapsedHeight = this.calculateCollapsedHeight({ wrapper: this.collapsibleTextNode, linesAmount: this.visibleLinesAmount })

        if (this.isElementClamped(this.collapsibleTextNode)) {
            this.readMoreBtn.classList.remove(this.expandButtonHiddenClassName)
            this.collapsibleTextNode.classList.add(this.textCollapsedClassName)
            this.readMoreBtn.innerText = this.readMoreText
        } else {
            this.readMoreBtn.classList.add(this.expandButtonHiddenClassName)
            this.collapsibleTextNode.classList.remove(this.textCollapsedClassName)
            this.readMoreBtn.innerText = this.readLessText
        }

        this.collapsibleTextNode.style.maxHeight = calculatedCollapsedHeight + 'px'
    }
}

customElements.define('collapsible-description', CollapsibleDescription);